import { ApiClient } from '../api/client';
import { captureLocator, resolveLocator } from '../core/locator';
import { captureRegion } from '../core/screenshot';
import { normalizeUrl } from '../core/url';
import type { Pin, WidgetConfig } from '../types';
import { h } from './dom';
import { STYLES } from './styles';

const HOST_ID = 'feedback-pro-root';

interface PendingAttachment {
  blob: Blob;
  name: string;
  url: string; // object URL for the thumbnail
}

export class Widget {
  private readonly api: ApiClient;
  private root!: ShadowRoot;
  private markersLayer!: HTMLElement;
  private toolbarBtn!: HTMLButtonElement;
  private panel: HTMLElement | null = null;
  private capture: HTMLElement | null = null;

  private pins: Pin[] = [];
  private markers = new Map<string, HTMLElement>();
  private feedbackMode = false;
  private repositionScheduled = false;
  private accent = '#4f46e5';
  private position: 'bottom-right' | 'bottom-left' = 'bottom-right';
  private showBranding = false;

  constructor(private readonly config: WidgetConfig) {
    this.api = new ApiClient(config);
  }

  async start(): Promise<void> {
    // Validate the key first; if it fails, stay completely silent.
    let boot;
    try {
      boot = await this.api.bootstrap();
    } catch {
      return;
    }

    // Apply per-project widget settings.
    const settings = boot.settings ?? {};
    if (settings.enabled === false) return; // widget disabled for this project

    // Invite-only projects stay dormant unless a valid client session is
    // present — anonymous visitors never see the feedback UI.
    if (boot.requires_session && !boot.session?.valid) return;

    if (typeof settings.accent_color === 'string') this.accent = settings.accent_color;
    if (settings.button_position === 'bottom-left') this.position = 'bottom-left';
    this.showBranding = boot.show_branding === true;

    this.mount();
    await this.loadPins();

    window.addEventListener('scroll', this.scheduleReposition, { passive: true });
    window.addEventListener('resize', this.scheduleReposition, { passive: true });

    // Deep link from the dashboard "View on site" link.
    if (this.config.focusPinId) {
      this.focusPin(this.config.focusPinId);
    }
  }

  /** Scroll to a pin, flash its marker, and open its thread (deep link). */
  private focusPin(pinId: string): void {
    const pin = this.pins.find((p) => p.id === pinId);
    if (!pin) return;

    const pos = resolveLocator(pin.locator, this.absFallback(pin));
    if (pos) {
      window.scrollTo({ top: Math.max(0, pos.y - window.innerHeight / 2), behavior: 'smooth' });
    }

    const marker = this.markers.get(pinId);
    if (marker) {
      marker.style.outline = '3px solid #fde047';
      marker.style.outlineOffset = '2px';
      setTimeout(() => {
        marker.style.outline = '';
        marker.style.outlineOffset = '';
      }, 2500);
    }

    this.openThread(pin);
  }

  // ── Mounting ──────────────────────────────────────────────────────────────

  private mount(): void {
    let host = document.getElementById(HOST_ID);
    if (host) host.remove();

    host = document.createElement('div');
    host.id = HOST_ID;
    host.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:0;';
    document.body.appendChild(host);

    this.root = host.attachShadow({ mode: 'open' });
    const style = h('style', {}, [STYLES]);
    this.root.appendChild(style);

    // Override the accent color from project settings.
    (this.root.host as HTMLElement).style.setProperty('--fp-accent', this.accent);

    this.markersLayer = h('div', { class: 'fp-markers' });
    this.root.appendChild(this.markersLayer);

    this.toolbarBtn = h(
      'button',
      { class: 'fp-btn', onclick: () => this.toggleFeedbackMode() },
      ['💬 Feedback'],
    );

    const toolbarChildren: Array<Node> = [this.toolbarBtn];
    if (this.showBranding) {
      toolbarChildren.unshift(h('span', { class: 'fp-badge' }, ['Powered by Feedback Pro']));
    }
    const toolbarClass = this.position === 'bottom-left' ? 'fp-toolbar fp-left' : 'fp-toolbar';
    this.root.appendChild(h('div', { class: toolbarClass }, toolbarChildren));
  }

  // ── Pins ──────────────────────────────────────────────────────────────────

  private async loadPins(): Promise<void> {
    try {
      this.pins = await this.api.listPins(normalizeUrl(window.location.href));
    } catch {
      this.pins = [];
      return;
    }
    this.renderMarkers();
  }

  private renderMarkers(): void {
    this.markersLayer.replaceChildren();
    this.markers.clear();
    this.pins.forEach((pin, i) => this.addMarker(pin, i + 1));
  }

  private addMarker(pin: Pin, index: number): void {
    const pos = resolveLocator(pin.locator, this.absFallback(pin));
    if (!pos) return;

    const marker = h(
      'div',
      {
        class: 'fp-marker',
        'data-status': pin.status,
        onclick: (e) => {
          e.stopPropagation();
          this.openThread(pin);
        },
      },
      [h('span', {}, [String(index)])],
    );
    marker.style.left = `${pos.x}px`;
    marker.style.top = `${pos.y}px`;

    this.markersLayer.appendChild(marker);
    this.markers.set(pin.id, marker);
  }

  private absFallback(pin: Pin): { x: number; y: number } | undefined {
    if (pin.coordinate_x != null && pin.coordinate_y != null) {
      return { x: pin.coordinate_x, y: pin.coordinate_y };
    }
    return undefined;
  }

  private scheduleReposition = (): void => {
    if (this.repositionScheduled) return;
    this.repositionScheduled = true;
    requestAnimationFrame(() => {
      this.repositionScheduled = false;
      for (const pin of this.pins) {
        const marker = this.markers.get(pin.id);
        if (!marker) continue;
        const pos = resolveLocator(pin.locator, this.absFallback(pin));
        if (pos) {
          marker.style.left = `${pos.x}px`;
          marker.style.top = `${pos.y}px`;
        }
      }
    });
  };

  // ── Feedback (pin placement) mode ───────────────────────────────────────────

  private toggleFeedbackMode(): void {
    this.feedbackMode ? this.exitFeedbackMode() : this.enterFeedbackMode();
  }

  private enterFeedbackMode(): void {
    this.feedbackMode = true;
    this.toolbarBtn.classList.add('fp-active');
    this.toolbarBtn.textContent = '✕ Cancel';
    this.closePanel();

    this.capture = h('div', { class: 'fp-capture', onclick: (e) => this.onCaptureClick(e as MouseEvent) });
    this.root.appendChild(this.capture);
    this.root.appendChild(h('div', { class: 'fp-hint' }, ['Click anywhere on the page to leave feedback']));
  }

  private exitFeedbackMode(): void {
    this.feedbackMode = false;
    this.toolbarBtn.classList.remove('fp-active');
    this.toolbarBtn.textContent = '💬 Feedback';
    this.capture?.remove();
    this.capture = null;
    this.root.querySelector('.fp-hint')?.remove();
  }

  private onCaptureClick(e: MouseEvent): void {
    // The capture overlay sits on top of the page, so elementFromPoint would
    // return our own shadow host. Hide it momentarily to read the real element
    // under the pointer.
    const prevDisplay = this.capture?.style.display ?? '';
    if (this.capture) this.capture.style.display = 'none';
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (this.capture) this.capture.style.display = prevDisplay;

    const locator = captureLocator(target ?? document.body, e.clientX, e.clientY);
    this.exitFeedbackMode();
    this.openComposer(locator, e.clientX + window.scrollX, e.clientY + window.scrollY);
  }

  // ── New-pin composer ─────────────────────────────────────────────────────────

  private openComposer(locator: ReturnType<typeof captureLocator>, absX: number, absY: number): void {
    this.closePanel();

    const attachments: PendingAttachment[] = [];

    const nameInput = h('input', { class: 'fp-input', placeholder: 'Your name (optional)', value: this.config.reviewerName ?? '' });
    const textarea = h('textarea', { class: 'fp-textarea', placeholder: 'Describe the issue…' });
    const thumbs = h('div', { class: 'fp-attachments' }, []);

    const renderThumbs = () => {
      thumbs.replaceChildren();
      attachments.forEach((att, i) => {
        const remove = h('button', {
          class: 'fp-thumb-remove',
          title: 'Remove',
          onclick: () => {
            URL.revokeObjectURL(att.url);
            attachments.splice(i, 1);
            renderThumbs();
          },
        }, ['×']);
        const thumb = h('div', { class: 'fp-thumb' }, [h('img', { src: att.url, alt: att.name }), remove]);
        thumbs.appendChild(thumb);
      });
    };

    // Hidden file input for "upload from your device".
    const fileInput = h('input', { type: 'file', accept: 'image/*', style: 'display:none' }) as HTMLInputElement;
    fileInput.addEventListener('change', () => {
      const files = Array.from(fileInput.files ?? []);
      for (const file of files) {
        attachments.push({ blob: file, name: file.name, url: URL.createObjectURL(file) });
      }
      fileInput.value = '';
      renderThumbs();
    });

    const captureBtn = h('button', {
      type: 'button',
      class: 'fp-attach-btn',
      onclick: () => this.startRegionCapture((blob) => {
        if (blob) {
          attachments.push({ blob, name: `screenshot-${attachments.length + 1}.png`, url: URL.createObjectURL(blob) });
          renderThumbs();
        }
      }),
    }, ['📷 Capture area']);

    const uploadBtn = h('button', {
      type: 'button',
      class: 'fp-attach-btn',
      onclick: () => fileInput.click(),
    }, ['📎 Upload image']);

    const submit = h(
      'button',
      {
        class: 'fp-btn',
        onclick: async () => {
          const body = (textarea as HTMLTextAreaElement).value.trim();
          if (!body) return;
          (submit as HTMLButtonElement).disabled = true;
          (submit as HTMLButtonElement).textContent = 'Sending…';
          await this.submitPin({
            locator,
            absX,
            absY,
            body,
            reviewerName: (nameInput as HTMLInputElement).value.trim() || undefined,
            attachments,
          });
        },
      },
      ['Send'],
    );

    this.panel = this.buildPanel('New feedback', [
      h('div', { class: 'fp-form' }, [
        nameInput,
        textarea,
        h('div', { class: 'fp-attach-actions' }, [captureBtn, uploadBtn]),
        thumbs,
        fileInput,
        h('div', { class: 'fp-form-actions' }, [
          h('button', { class: 'fp-link', onclick: () => this.closePanel() }, ['Cancel']),
          submit,
        ]),
      ]),
    ]);
    this.root.appendChild(this.panel);
  }

  /** Drag-to-select a page region; captures + crops it to a PNG blob. */
  private startRegionCapture(onDone: (blob: Blob | null) => void): void {
    const panel = this.panel;
    if (panel) panel.style.display = 'none';

    const box = h('div', { class: 'fp-region-box' });
    box.style.display = 'none';
    const overlay = h('div', { class: 'fp-region-capture' }, [box]);
    const hint = h('div', { class: 'fp-hint' }, ['Drag to select an area to capture']);
    this.root.appendChild(overlay);
    this.root.appendChild(hint);

    let startX = 0;
    let startY = 0;
    let dragging = false;

    const draw = (e: MouseEvent) => {
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      box.style.left = `${x}px`;
      box.style.top = `${y}px`;
      box.style.width = `${Math.abs(e.clientX - startX)}px`;
      box.style.height = `${Math.abs(e.clientY - startY)}px`;
    };

    overlay.addEventListener('mousedown', (e) => {
      const m = e as MouseEvent;
      dragging = true;
      startX = m.clientX;
      startY = m.clientY;
      box.style.display = 'block';
      draw(m);
    });
    overlay.addEventListener('mousemove', (e) => {
      if (dragging) draw(e as MouseEvent);
    });
    overlay.addEventListener('mouseup', async (e) => {
      dragging = false;
      const m = e as MouseEvent;
      const left = Math.min(startX, m.clientX);
      const top = Math.min(startY, m.clientY);
      const width = Math.abs(m.clientX - startX);
      const height = Math.abs(m.clientY - startY);
      overlay.remove();
      hint.remove();

      if (width < 5 || height < 5) {
        if (panel) panel.style.display = '';
        onDone(null);
        return;
      }

      const blob = await captureRegion({
        x: left + window.scrollX,
        y: top + window.scrollY,
        width,
        height,
      });
      if (panel) panel.style.display = '';
      onDone(blob);
    });
  }

  private async submitPin(input: {
    locator: ReturnType<typeof captureLocator>;
    absX: number;
    absY: number;
    body: string;
    reviewerName?: string;
    attachments: PendingAttachment[];
  }): Promise<void> {
    try {
      const pin = await this.api.createPin({
        page_url: normalizeUrl(window.location.href),
        locator: input.locator,
        coordinate_x: input.absX,
        coordinate_y: input.absY,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        user_agent: navigator.userAgent,
        body: input.body,
        reviewer_name: input.reviewerName,
      });

      // Upload any attachments the reviewer added (best-effort, never blocks).
      for (const att of input.attachments) {
        try {
          await this.api.uploadAttachment(pin.id, att.blob, att.name);
        } catch {
          /* best-effort */
        }
        URL.revokeObjectURL(att.url);
      }

      this.pins.push(pin);
      this.addMarker(pin, this.pins.length);
      this.closePanel();
      this.toast('Feedback sent — thank you!');
    } catch {
      this.toast('Could not send feedback. Please try again.');
    }
  }

  // ── Existing-pin thread ──────────────────────────────────────────────────────

  private openThread(pin: Pin): void {
    this.closePanel();

    const thread = h('div', { class: 'fp-thread' }, []);
    this.renderComments(thread, pin);

    const textarea = h('textarea', { class: 'fp-textarea', placeholder: 'Reply…' });
    const send = h(
      'button',
      {
        class: 'fp-btn',
        onclick: async () => {
          const body = (textarea as HTMLTextAreaElement).value.trim();
          if (!body) return;
          (send as HTMLButtonElement).disabled = true;
          try {
            const comment = await this.api.addComment(pin.id, {
              body,
              reviewer_name: this.config.reviewerName,
            });
            pin.comments = [...(pin.comments ?? []), comment];
            this.renderComments(thread, pin);
            (textarea as HTMLTextAreaElement).value = '';
          } catch {
            this.toast('Could not send reply.');
          } finally {
            (send as HTMLButtonElement).disabled = false;
          }
        },
      },
      ['Reply'],
    );

    this.panel = this.buildPanel('Feedback thread', [
      thread,
      h('div', { class: 'fp-form' }, [textarea, h('div', { class: 'fp-form-actions' }, [send])]),
    ]);
    this.root.appendChild(this.panel);
  }

  private renderComments(thread: HTMLElement, pin: Pin): void {
    const comments = pin.comments ?? [];
    thread.replaceChildren();

    if (comments.length === 0) {
      thread.appendChild(h('div', { class: 'fp-empty' }, ['No comments yet.']));
      return;
    }

    for (const c of comments) {
      const meta = h('div', { class: 'fp-comment-meta' }, []);
      const name = h('span', c.is_team ? { class: 'fp-team' } : {}, [c.author_name || 'Anonymous']);
      meta.append(name);
      thread.appendChild(
        h('div', { class: 'fp-comment' }, [meta, h('div', { class: 'fp-comment-body' }, [c.body])]),
      );
    }
    thread.scrollTop = thread.scrollHeight;
  }

  // ── Panel scaffolding ────────────────────────────────────────────────────────

  private buildPanel(title: string, content: Array<Node>): HTMLElement {
    return h('div', { class: 'fp-panel' }, [
      h('div', { class: 'fp-panel-head' }, [
        h('div', { class: 'fp-panel-title' }, [title]),
        h('button', { class: 'fp-close', onclick: () => this.closePanel() }, ['×']),
      ]),
      ...content,
    ]);
  }

  private closePanel(): void {
    this.panel?.remove();
    this.panel = null;
  }

  private toast(message: string): void {
    const t = h('div', { class: 'fp-toast' }, [message]);
    this.root.appendChild(t);
    setTimeout(() => t.remove(), 3000);
  }
}
